import React, { useState, useEffect } from 'react';
import creditsBg from '../assets/credits.jpg';
import { useLanguage } from '../contexts/LanguageContext';
import { getConfig } from '../config/gameConfig';
import LanguageSelector from './LanguageSelector';

export default function Credits({ onRequestFullscreenAndPlay }) {
  const { language } = useLanguage();
  const [isFullscreen, setIsFullscreen] = useState(false);

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
    };
  }, []);

  const toggleFullscreen = async () => {
    try {
      if (!document.fullscreenElement) {
        await document.documentElement.requestFullscreen();
      } else {
        await document.exitFullscreen();
      }
    } catch (err) {
      if (getConfig('debugMode')) {
        console.log('Fullscreen toggle failed:', err);
      }
    }
  };

  const handlePlay = () => {
    onRequestFullscreenAndPlay();
  };

  return (
    <div
      className="credits-screen"
      style={{
        width: '100vw',
        height: '100vh',
        backgroundImage: `url(${creditsBg})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <style>{`
        .sparkle {
          position: absolute;
          width: 6px;
          height: 6px;
          background: radial-gradient(circle, #fffbe6 60%, #ffe066 100%, transparent 100%);
          border-radius: 50%;
          opacity: 0.8;
          pointer-events: none;
          animation: sparkle-float 2.8s linear infinite;
        }
        @keyframes sparkle-float {
          0% {
            opacity: 0;
            transform: translate(0, 0) scale(0.7) rotate(0deg);
          }
          10% {
            opacity: 1;
          }
          80% {
            opacity: 1;
          }
          100% {
            opacity: 0;
            /* The transform will be set inline for each sparkle for random direction */
          }
        }
        
        .fullscreen-btn {
          position: absolute;
          top: 40px;
          right: 40px;
          width: 60px;
          height: 60px;
          background: rgba(0, 0, 0, 0.5);
          border: none;
          borderRadius: 50%;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 12px;
          transition: all 0.3s ease;
          z-index: 10;
          backdrop-filter: blur(10px);
        }
        
        .play-btn {
          position: absolute;
          bottom: 40px;
          right: 40px;
          z-index: 10;
        }
        
        @media screen and (max-width: 767px) {
          .fullscreen-btn {
            top: 20px;
            right: 20px;
            width: 50px;
            height: 50px;
            padding: 10px;
          }
          
          .play-btn {
            bottom: 20px;
            right: 20px;
            padding: 12px 30px !important;
            font-size: 1rem !important;
          }
        }
      `}</style>
      {/* Sparkles */}
      {Array.from({ length: 200 }).map((_, i) => {
        const startLeft = Math.random() * 98;
        const startTop = Math.random() * 98;
        const dx = (Math.random() - 0.5) * 120;
        const dy = (Math.random() - 0.5) * 120;
        const delay = Math.random() * 2.8;
        const size = Math.random() * 4 + 4;
        return (
          <div
            key={i}
            className="sparkle"
            style={{
              left: `${startLeft}%`,
              top: `${startTop}%`,
              width: size,
              height: size,
              animationDelay: `${delay}s`,
              animationDuration: '2.8s',
              animationName: 'sparkle-float',
              '--sparkle-end-transform': `translate(${dx}px, ${dy}px) scale(1.2) rotate(30deg)`
            }}
          />
        );
      })}
      
      {/* Language selector (bottom left) */}
      <LanguageSelector />
      
      {/* Fullscreen toggle button (top right) */}
      <button 
        className="fullscreen-btn"
        onClick={toggleFullscreen}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)';
          e.currentTarget.style.transform = 'scale(1.1)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = 'rgba(0, 0, 0, 0.5)';
          e.currentTarget.style.transform = 'scale(1)';
        }}
        title={isFullscreen ? 'Exit Fullscreen' : 'Enter Fullscreen'}
      >
        <svg 
          xmlns="http://www.w3.org/2000/svg" 
          viewBox="0 0 122.88 122.88"
          style={{ width: '100%', height: '100%', fill: 'white' }}
        >
          <path fillRule="evenodd" d="M61.44,0A61.44,61.44,0,1,1,0,61.44,61.44,61.44,0,0,1,61.44,0ZM34.23,50.26a3.29,3.29,0,1,1-6.58,0V35.32A3.29,3.29,0,0,1,30.94,32H46.16a3.29,3.29,0,1,1,0,6.58H38.89L52,51.65a3.28,3.28,0,1,1-4.63,4.65L34.23,43.24v7ZM47.58,86a3.3,3.3,0,0,1,0,6.59H32.64a3.29,3.29,0,0,1-3.29-3.29V74a3.29,3.29,0,1,1,6.58,0V81.3L49,68.23a3.28,3.28,0,1,1,4.65,4.63L40.56,86ZM88.65,72.62a3.29,3.29,0,0,1,6.58,0V87.56a3.29,3.29,0,0,1-3.29,3.29H76.72a3.29,3.29,0,1,1,0-6.58H84l-13.08-13a3.28,3.28,0,0,1,4.63-4.65L88.65,79.64v-7ZM75.3,36.92a3.3,3.3,0,0,1,0-6.59H90.24a3.29,3.29,0,0,1,3.29,3.29V48.84a3.29,3.29,0,1,1-6.58,0V41.58l-13,13.07A3.28,3.28,0,1,1,69.26,50l13.06-13.1Z"/>
        </svg>
      </button>

      {/* Let's Play button (bottom right) */}
      <button 
        onClick={handlePlay}
        className="main-btn play-btn"
        style={{
          padding: '15px 40px',
          fontSize: '1.2rem',
          cursor: 'pointer'
        }}
      >
        {language.credits.playButton}
      </button>
    </div>
  );
} 